'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { createLogger } from '@/shared/lib/logger';
import type { AssistantMoment, AssistantScreen } from '@/types/assistant';
import { assistantService } from '../api/assistant.service';
import { useAssistantStore } from '../model/assistant.store';
import { ensureThread } from './use-assistant-thread';

const logger = createLogger('Assistant');

/** Screens a building's moment may appear on (ADR-001 §1). */
const MOMENT_SCREENS: AssistantScreen[] = ['building', 'brief', 'design', 'render'];

/**
 * The one proactive moment the API offers for this building, if any. Which
 * moment, whether at all, and as a bubble or a badge on the AI button is the
 * server's decision; the client reports what the user did with it:
 *
 * - `shown` when the bubble or the badge is on screen,
 * - `dismissed` when the bubble is closed,
 * - `engaged` with the thread when the user opens it; the server then writes
 *   the moment into the thread as the assistant's message.
 *
 * A `render_note` keeps its key from render to render, so a moment is told
 * apart by key and render.
 */
export function useAssistantMoment(
  screen: AssistantScreen,
  buildingId: string | null
) {
  const queryClient = useQueryClient();
  const isOpen = useAssistantStore((s) => s.isOpen);
  // Retired here at once; the server already retired it with the outcome.
  const [handled, setHandled] = useState<Set<string>>(() => new Set());
  const reportedShown = useRef<Set<string>>(new Set());

  const eligible = !!buildingId && MOMENT_SCREENS.includes(screen);
  const { data } = useQuery({
    queryKey: cacheKeys.assistant.moment(buildingId ?? ''),
    queryFn: () => assistantService.listMoments(buildingId!),
    enabled: eligible && !isOpen,
    staleTime: 0,
  });

  const candidate: AssistantMoment | null = data?.[0] ?? null;
  const moment =
    eligible && !isOpen && candidate && candidate.attributes.building_id === buildingId && !handled.has(keyOf(candidate))
      ? candidate
      : null;

  const retire = useCallback((target: AssistantMoment) => {
    setHandled((prev) => new Set(prev).add(keyOf(target)));
  }, []);

  const momentKey = moment ? keyOf(moment) : null;
  useEffect(() => {
    if (!moment || !momentKey || reportedShown.current.has(momentKey)) return;
    reportedShown.current.add(momentKey);
    assistantService
      .recordMoment(moment, 'shown')
      .catch((error) => logger.warn('Moment outcome not recorded', { error: String(error) }));
  }, [moment, momentKey]);

  const dismiss = useCallback(() => {
    if (!moment) return;
    retire(moment);
    assistantService
      .recordMoment(moment, 'dismissed')
      .catch((error) => logger.warn('Moment outcome not recorded', { error: String(error) }));
  }, [moment, retire]);

  /** Open the conversation on the moment; an option tapped is sent as the reply. */
  const engage = useCallback(
    async (reply?: string) => {
      if (!moment) return;
      retire(moment);
      useAssistantStore.getState().open();
      try {
        const thread = await ensureThread(queryClient, moment.attributes.building_id);
        await assistantService.recordMoment(moment, 'engaged', thread.id);
        await queryClient.refetchQueries({ queryKey: cacheKeys.assistant.messages(thread.id) });
      } catch (error) {
        logger.warn('Moment outcome not recorded', { error: String(error) });
      }
      // Only now: the reply must land in the thread after the moment it
      // answers, which `engaged` has just written there.
      if (reply) useAssistantStore.getState().open(reply);
    },
    [moment, queryClient, retire]
  );

  return { moment, dismiss, engage };
}

const keyOf = (moment: AssistantMoment) =>
  `${moment.id}:${moment.attributes.building_id}:${moment.attributes.render_id ?? ''}`;
